import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { 
  Download, 
  Zap,
  Calendar,
  Activity,
  Package,
  ShoppingCart
} from "lucide-react";
import { useCredits } from '@/hooks/useCredits';
import { formatNumber, formatDate } from '@/lib/utils';
import { CreditTransaction } from '@/hooks/useCredits';
import { cn } from '@/lib/utils';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

interface UsageSummary {
  totalConsumed: number;
  byCategory: Record<string, number>;
  byDay: Array<{ date: string; amount: number }>;
}

export function CreditUsageDashboard() {
  const { balance, fetchTransactions, getUsageSummary } = useCredits();
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('month');
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch transactions
        const transResult = await fetchTransactions({
          limit: pageSize,
          offset: currentPage * pageSize
        });
        setTransactions(transResult.transactions);
        setTransactionTotal(transResult.total);

        // Fetch usage summary
        const summary = await getUsageSummary(period);
        setUsageSummary(summary);
      } catch (error) {
        console.error('Error fetching credit data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period, currentPage, fetchTransactions, getUsageSummary]);

  // Calculate metrics
  const totalCredits = balance?.total_credits || 0;
  const usedCredits = totalCredits - ((balance?.plan_credits || 0) + (balance?.purchased_credits || 0));
  const percentageUsed = totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0;

  // Format data for charts
  const categoryData = usageSummary?.byCategory 
    ? Object.entries(usageSummary.byCategory).map(([name, value]) => ({
        name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value
      }))
    : [];

  const dailyData = usageSummary?.byDay || [];

  // Export functionality
  const exportData = () => {
    const csvContent = [
      ['Date', 'Type', 'Category', 'Amount', 'Balance After', 'Description'],
      ...transactions.map(tx => [
        formatDate(tx.created_at),
        tx.type,
        tx.category || 'other',
        tx.amount,
        tx.balance_after,
        tx.description
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit-usage-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading && !balance) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">Credit Usage Dashboard</h2>
          <p className="text-muted-foreground">
            Monitor your credit consumption and purchase history
          </p>
        </div>
        <Button onClick={exportData} variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Export Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Credits</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalCredits)}</div>
            <div className="flex items-center text-xs text-muted-foreground">
              <span>{formatNumber(balance?.plan_credits || 0)} plan</span>
              {(balance?.purchased_credits || 0) > 0 && (
                <span className="ml-2">+ {formatNumber(balance?.purchased_credits || 0)} purchased</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Credits Used</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(usedCredits)}</div>
            <Progress value={percentageUsed} className="mt-2 h-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {Math.round(percentageUsed)}% of total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This {period}</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(usageSummary?.totalConsumed || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              credits consumed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={cn(
              "text-2xl font-bold",
              percentageUsed >= 95 && "text-red-600",
              percentageUsed >= 80 && percentageUsed < 95 && "text-yellow-600"
            )}>
              {formatNumber(totalCredits - usedCredits)}
            </div>
            <p className="text-xs text-muted-foreground">
              credits remaining
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="usage" className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="usage">Usage Trends</TabsTrigger>
            <TabsTrigger value="breakdown">Category Breakdown</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
          </TabsList>
          <Select value={period} onValueChange={(value: any) => setPeriod(value)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <TabsContent value="usage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Daily Usage Trend</CardTitle>
              <CardDescription>
                Credit consumption over the selected period
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: number) => formatNumber(value)}
                    labelFormatter={(label) => new Date(label).toLocaleDateString()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="breakdown" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Usage by Category</CardTitle>
                <CardDescription>
                  How credits are being consumed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {categoryData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatNumber(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Category Details</CardTitle>
                <CardDescription>
                  Credit usage breakdown
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {categoryData.map((category, index) => (
                    <div key={category.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm font-medium">{category.name}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {formatNumber(category.value)} credits
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
              <CardDescription>
                Detailed credit transaction history
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between py-3 border-b last:border-0">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{transaction.description}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {transaction.type.replace(/_/g, ' ')}
                        </Badge>
                        {transaction.category && (
                          <Badge variant="secondary" className="text-xs">
                            {transaction.category.replace(/_/g, ' ')}
                          </Badge>
                        )}
                        <span>{formatDate(transaction.created_at)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={cn(
                        "text-sm font-medium",
                        transaction.amount > 0 ? "text-green-600" : "text-red-600"
                      )}>
                        {transaction.amount > 0 ? '+' : ''}{formatNumber(Math.abs(transaction.amount))}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Balance: {formatNumber(transaction.balance_after)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {transactionTotal > pageSize && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, transactionTotal)} of {transactionTotal}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => p + 1)}
                      disabled={(currentPage + 1) * pageSize >= transactionTotal}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Purchase Credits CTA */}
      {percentageUsed >= 80 && (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Running Low on Credits
            </CardTitle>
            <CardDescription>
              You've used {Math.round(percentageUsed)}% of your credits. Consider purchasing more to continue using all features.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.href = '/billing'}>
              Purchase Credits
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}